import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostBinding,
  inject,
  signal,
} from '@angular/core';
import {
  AiFeaturesService,
  IQBOOST_CONVERSATION_STORE,
  IqBoostPromptCategory,
  IQBoostSingleHouseholdStore,
  LanguageHelper,
  MaximizerAiService,
} from '@maximizer/ai/data-access';
import {
  AddressBookService,
  OpportunityService,
} from '@maximizer/core/shared/data-access';
import { EntryType } from '@maximizer/core/shared/domain';
import { EmbeddedComponent } from '@maximizer/core/shared/ui/components/base';
import { ActionEventType, OpenEntityArgs } from '@maximizer/core/shared/ui/config';
import { isNullOrEmpty } from '@maximizer/shared/util';
import { TranslatePipe } from '@ngx-translate/core';
import {
  catchError,
  finalize,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { IQBoostArgs, IQBoostTextVersion } from '../../classes/iqboost';
import { IqboostChatDialogComponent } from '../iqboost-chat-dialog/iqboost-chat-dialog.component';
import { IQBoostChatScope } from '../iqboost-chat-scope';
import { IQBoostStarterPrompt } from '../iqboost-starter-prompts/iqboost-starter-prompts.component';

interface IQBoostEntry {
  key: string;
  type: EntryType;
}

// Keeps the orchestrator's lookups on the entry. An instruction to it, so never translated.
const entryInstruction = ({ key, type }: IQBoostEntry): string =>
  `The entity type is: ${type} and its key is: ${key}, ` +
  'use them to filter out the data using the MCP before trying to respond the user question.';

// Each prompt takes the icon of its catalog category; the categories themselves are not shown.
const toStarterPrompts = (categories: IqBoostPromptCategory[]): IQBoostStarterPrompt[] =>
  categories.flatMap((category) =>
    category.prompts
      .filter((prompt) => !isNullOrEmpty(prompt.title))
      .map((prompt) => ({
        text: prompt.title,
        iconClass: category.iconStyle,
        promptName: prompt.name,
      })),
  );

@Component({
  selector: 'ai-iqboost-single-household',
  templateUrl: './iqboost-single-household.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: IQBOOST_CONVERSATION_STORE, useExisting: IQBoostSingleHouseholdStore }],
  imports: [IqboostChatDialogComponent, TranslatePipe],
})
export class IqboostSingleHouseholdComponent extends EmbeddedComponent<OpenEntityArgs> {
  @HostBinding('class') hostClasses = 'ai-ng ai-headless-ng';

  private readonly aiFeatures = inject(AiFeaturesService);
  private readonly maximizerAi = inject(MaximizerAiService);
  private readonly addressBook = inject(AddressBookService);
  private readonly opportunity = inject(OpportunityService);
  private readonly conversation = inject(IQBoostSingleHouseholdStore);

  readonly isOpen = signal(false);
  readonly entry = signal<IQBoostEntry | null>(null);
  readonly entryName = signal('');
  // The opportunity the dialog was opened from, if any.
  readonly opportunityName = signal('');
  readonly prompts = signal<IQBoostStarterPrompt[]>([]);
  readonly loadingPrompts = signal(false);
  // Opened to show more of the summary widget's answer: the first prompt is asked right away.
  readonly autoAsk = signal(false);
  // Kept from one opening to the next.
  readonly richUi = signal(true);

  protected readonly scope = computed<IQBoostChatScope>(() => {
    const entry = this.entry();
    return {
      mode: 'single',
      thread: {
        source: 'iqboost-single-household',
        entry_type: entry?.type ?? '',
        entry_key: entry?.key ?? '',
      },
      instruction: entry ? entryInstruction(entry) : '',
    };
  });
  // "Cameron Family (Renewal)", as the classic dialog shows it.
  protected readonly scopeLabel = computed(() => {
    const name = this.entryName();
    const opportunity = this.opportunityName();
    return name && opportunity ? `${name} (${opportunity})` : name;
  });
  protected readonly textVersion = computed(() =>
    this.aiFeatures.isSLE()
      ? IQBoostTextVersion.defaultVersion
      : IQBoostTextVersion.faVersion,
  );
  protected readonly subtitleKey = computed(
    () => `iqboost.singleHousehold.subtitle.${this.textVersion()}`,
  );
  protected readonly placeholderKey = computed(() =>
    this.textVersion() === IQBoostTextVersion.faVersion
      ? 'iqboost.buttons.askQuestionNoPrompt.faVersion.singleHousehold'
      : 'iqboost.buttons.askQuestionNoPrompt.defaultVersion.singleContact',
  );

  // A show on another entry drops what was being loaded for the last one.
  private readonly entryLoads = new Subject<{ key: string; fromKey?: string }>();

  constructor() {
    super(inject(ElementRef));
    this.addSubscription(
      this.entryLoads.pipe(switchMap((entry) => this.loadEntry(entry))).subscribe(),
    );
  }

  override doAction(name: string, args?: IQBoostArgs): void {
    if (name === ActionEventType.Show || name === ActionEventType.Initialize) {
      this.open(args);
    } else {
      super.doAction(name, args);
    }
  }

  // The dialog is modal: while it is open no other entry can be asked for, so a show is ignored.
  private open(args?: IQBoostArgs): void {
    const key = args?.key;
    if (!key || this.isOpen()) {
      return;
    }
    const entry: IQBoostEntry = { key, type: args.type || 'addressBook' };

    this.conversation.useEntry(key);
    if (args.autoOpen) {
      // To show more of the summary widget's answer: its first prompt starts a new conversation.
      this.conversation.startNewConversation();
    }
    this.entry.set(entry);
    this.autoAsk.set(args.autoOpen ?? false);
    this.entryLoads.next({ key, fromKey: args.fromKey });
    this.isOpen.set(true);
  }

  // The header's names and the prompts, each shown as soon as it is there.
  private loadEntry({ key, fromKey }: { key: string; fromKey?: string }): Observable<unknown> {
    this.entryName.set('');
    this.opportunityName.set('');
    this.prompts.set([]);
    this.loadingPrompts.set(true);

    return merge(
      // A name that cannot be read is left out of the header.
      forkJoin({
        entry: this.addressBook.getDisplayValue(key),
        opportunity: fromKey ? this.opportunity.getDisplayValue(fromKey) : of(null),
      }).pipe(
        catchError(() => of({ entry: null, opportunity: null })),
        tap(({ entry, opportunity }) => {
          this.entryName.set(entry ?? '');
          this.opportunityName.set(opportunity ?? '');
        }),
      ),
      // Without the catalog the landing view offers no prompts; the question box still works.
      this.maximizerAi.getPrompts({ language: LanguageHelper.language(), type: 'single' }).pipe(
        map(toStarterPrompts),
        catchError(() => of([])),
        tap((prompts) => this.prompts.set(prompts)),
        finalize(() => this.loadingPrompts.set(false)),
      ),
    );
  }
}
